import { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import SectionTitle from '../components/ui/SectionTitle'
import Card from '../components/ui/Card'
import CTA from '../components/home/CTA'
import { useShopProducts, useShopCategories } from '../hooks/useShop'
import { useCart } from '../context/CartContext'

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.07, ease: 'easeOut' },
  }),
}

function ProductCard({ product, index }) {
  const { addItem, pendingId } = useCart()
  const isAdding = pendingId === product.id

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: '-40px' }}
      custom={index}
    >
      <Card className="overflow-hidden flex flex-col h-full">
        <Link to={`/shop/${product.id}`}>
          {/* Image */}
          <div className="aspect-[4/3] overflow-hidden bg-light-200">
            <img
              src={product.image?.url || '/placeholder.png'}
              alt={product.image?.alt || product.name}
              className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
            />
          </div>
        </Link>

        {/* Body */}
        <div className="p-6 flex flex-col flex-1">
          {/* Categories */}
          {product.categories?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {product.categories.map((cat) => (
                <span
                  key={cat.id}
                  className="px-3 py-0.5 rounded-full text-xs font-body bg-primary/10 text-primary"
                >
                  {cat.name}
                </span>
              ))}
            </div>
          )}

          <Link to={`/shop/${product.id}`}>
            <h3 className="font-display text-xl text-ink mb-2 leading-snug hover:text-primary transition-colors">
              {product.name}
            </h3>
          </Link>

          {product.short_description && (
            <p
              className="font-body text-sm text-ink-muted mb-4 line-clamp-3 flex-1"
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
          )}

          {/* Price + Sale badge */}
          <div className="mt-auto flex items-end justify-between gap-4">
            <div>
              {product.is_on_sale && product.regular_price && (
                <p className="font-body text-sm text-ink-muted line-through">
                  {product.currency_symbol}{Number(product.regular_price).toLocaleString()}
                </p>
              )}
              <p className="font-display text-2xl text-primary">
                {product.currency_symbol}{Number(product.price).toLocaleString()}
                <span className="text-sm text-ink-muted font-body ml-1">{product.currency}</span>
              </p>
            </div>

            {product.is_on_sale && (
              <span className="px-3 py-1 rounded-full text-xs font-body bg-accent/10 text-accent font-semibold">
                Sale
              </span>
            )}
          </div>

          {product.purchasable && product.in_stock ? (
            <button
              onClick={() => addItem(product.id)}
              disabled={isAdding}
              className={[
                'mt-5 w-full py-3 px-6 rounded-full font-body font-bold text-sm transition-all duration-300',
                isAdding
                  ? 'bg-primary/60 text-white cursor-wait'
                  : 'bg-primary text-white hover:bg-primary-dark hover:shadow-lg hover:shadow-primary/40 hover:-translate-y-0.5 active:translate-y-0',
              ].join(' ')}
            >
              {isAdding ? 'Adding…' : 'Add to Cart'}
            </button>
          ) : (
            <button disabled className="mt-5 w-full py-3 px-6 rounded-full font-body font-bold text-sm bg-light-300 text-ink-muted cursor-not-allowed">
              Out of Stock
            </button>
          )}
        </div>
      </Card>
    </motion.div>
  )
}

export default function Shop() {
  const [activeCategory, setActiveCategory] = useState('')

  const { data: categories } = useShopCategories()
  const { data: products, isLoading, isError } = useShopProducts(
    activeCategory ? { category: activeCategory } : {}
  )

  return (
    <>
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-primary via-purple-700 to-secondary py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none bg-[radial-gradient(circle_at_30%_50%,#fff,transparent_60%)]" />
        <div className="container-custom text-center relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="eyebrow inline-block mb-4 px-5 py-1.5 rounded-full text-xs bg-white/20 text-white border border-white/30"
          >
            Farafeero Shop
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6"
          >
            Everything Your Party Needs
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-body text-lg text-white/80 max-w-2xl mx-auto"
          >
            Browse our curated selection of party packages, add-ons, and entertainment options.
          </motion.p>
        </div>
      </section>

      {/* Products */}
      <section className="section-padding bg-gradient-to-b from-background to-light-300">
        <div className="container-custom">
          <SectionTitle
            eyebrow="Our Products"
            title="Shop Birthday Magic"
            subtitle="All the elements to make your child's celebration unforgettable."
          />

          {/* Category filter tabs */}
          {categories?.length > 0 && (
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              <button
                onClick={() => setActiveCategory('')}
                className={[
                  'px-5 py-2 rounded-full font-body text-sm transition-colors duration-200',
                  activeCategory === ''
                    ? 'bg-primary text-white'
                    : 'bg-white text-ink-muted border border-light-400 hover:border-primary hover:text-primary',
                ].join(' ')}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={[
                    'px-5 py-2 rounded-full font-body text-sm transition-colors duration-200',
                    activeCategory === cat.slug
                      ? 'bg-primary text-white'
                      : 'bg-white text-ink-muted border border-light-400 hover:border-primary hover:text-primary',
                  ].join(' ')}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          )}

          {/* Grid */}
          {isError && (
            <p className="text-center font-body text-ink-muted py-16">
              Could not load products. Please try again later.
            </p>
          )}

          {!isError && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {isLoading
                ? Array(6).fill(null).map((_, i) => (
                    <div key={i} className="aspect-[3/4] skeleton rounded-3xl" />
                  ))
                : products?.length > 0
                  ? products.map((product, i) => (
                      <ProductCard key={product.id} product={product} index={i} />
                    ))
                  : (
                    <p className="col-span-3 text-center font-body text-ink-muted py-16">
                      No products found{activeCategory ? ' in this category' : ''}.
                    </p>
                  )}
            </div>
          )}
        </div>
      </section>

      <CTA />
    </>
  )
}
